import { useState, useEffect } from "react";

interface ConnectionInfo {
  effectiveType: '2g' | '3g' | '4g' | 'slow-2g';
  downlink: number;
  rtt: number;
  saveData: boolean;
}

interface ProgressiveEnhancementState {
  isSlowConnection: boolean;
  shouldReduceAnimations: boolean;
  shouldLazyLoad: boolean;
  shouldOptimizeImages: boolean;
  connectionSpeed: 'fast' | 'medium' | 'slow';
}

/**
 * Hook for progressive enhancement based on connection quality
 * Optimizes UX for different network conditions
 */
export function useProgressiveEnhancement(): ProgressiveEnhancementState {
  const [state, setState] = useState<ProgressiveEnhancementState>({
    isSlowConnection: false,
    shouldReduceAnimations: false,
    shouldLazyLoad: true,
    shouldOptimizeImages: false,
    connectionSpeed: 'fast'
  });

  useEffect(() => {
    // Check if Network Information API is available
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    const updateConnectionInfo = () => {
      if (connection) {
        const effectiveType = connection.effectiveType;
        const saveData = connection.saveData;
        const downlink = connection.downlink || 0;
        
        // Determine connection speed
        let connectionSpeed: 'fast' | 'medium' | 'slow' = 'fast';
        let isSlowConnection = false;
        
        if (effectiveType === 'slow-2g' || effectiveType === '2g' || downlink < 0.5 || saveData) {
          connectionSpeed = 'slow';
          isSlowConnection = true;
        } else if (effectiveType === '3g' || downlink < 1.5) {
          connectionSpeed = 'medium';
        }
        
        setState({
          isSlowConnection,
          shouldReduceAnimations: isSlowConnection || saveData,
          shouldLazyLoad: true, // Always beneficial
          shouldOptimizeImages: isSlowConnection || saveData,
          connectionSpeed
        });
      } else {
        // Fallback: Use performance timing to estimate connection
        const timing = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (timing) {
          const loadTime = timing.loadEventEnd - timing.fetchStart;
          const isSlowConnection = loadTime > 3000; // Consider slow if > 3s load time
          
          setState({
            isSlowConnection,
            shouldReduceAnimations: isSlowConnection,
            shouldLazyLoad: true,
            shouldOptimizeImages: isSlowConnection,
            connectionSpeed: isSlowConnection ? 'slow' : 'fast'
          });
        }
      }
    };

    // Initial check
    updateConnectionInfo();

    // Listen for connection changes
    if (connection) {
      connection.addEventListener('change', updateConnectionInfo);
      return () => connection.removeEventListener('change', updateConnectionInfo);
    }
  }, []);

  return state;
}

/**
 * Hook to get optimized loading strategy based on connection
 */
export function useLoadingStrategy() {
  const { connectionSpeed, shouldLazyLoad } = useProgressiveEnhancement();
  
  return {
    // Delay non-critical renders on slow connections
    shouldDeferNonCritical: connectionSpeed === 'slow',
    
    // Use smaller bundle chunks on slow connections
    shouldCodeSplit: connectionSpeed !== 'fast',
    
    // Prioritize critical content
    shouldLazyLoad,
    
    // Reduce image quality on slow connections
    imageQuality: connectionSpeed === 'slow' ? 'low' : connectionSpeed === 'medium' ? 'medium' : 'high',
    
    // Animation strategy
    animationLevel: connectionSpeed === 'slow' ? 'minimal' : connectionSpeed === 'medium' ? 'reduced' : 'full'
  };
}